//@flow
import {useSelector} from 'react-redux';

import {endpoints} from './api';
import {
  createProductsBatchService,
  updateProductsBatchService,
  getProductsBatchCreateStatusService,
  getProductsBatchEditStatusService,
} from './promises';

import {AVAILABLE_PRODUCT_BATCH_LOAD_FORMATS} from '../../entities';
import {useState, useEffect} from 'react';

import {actions} from './duck';

import type {ProductBatchLoadTask} from '../../entities';
import type {RootState} from '..';
import {useActions} from '../../utils/globalStore';
import {uploadFileService} from '../file-storage/promises';

export function useProductBatchLoadTemplates() {
  return {
    createTemplateLink: `${endpoints.PRODUCT_BATCH_TEMPLATE}?template=CREATE`,
    editTemplateLink: `${endpoints.PRODUCT_BATCH_TEMPLATE}?template=EDIT`,
  };
}

export function useProductBatchLoadActions() {
  const [loading, setLoading] = useState(false);

  const setBatchTaskUpdate = useActions(actions.setBatchTaskUpdate);
  const setBatchTaskCreate = useActions(actions.setBatchTaskCreate);

  const update = async (file: File) => {
    const format = file.name.split('.').pop();

    if (!AVAILABLE_PRODUCT_BATCH_LOAD_FORMATS.includes(format)) {
      throw new Error('Formato de archivo no soportado');
    }

    setLoading(true);

    try {
      const uploadResult = await uploadFileService(file, {
        filePrefix: 'im/batch-product/edit',
        resourceType: 'XLSX',
        temp: true,
      });
      const [fileUploaded] = uploadResult;
      const result = await updateProductsBatchService({
        fileId: fileUploaded.id,
      });
      setBatchTaskUpdate(result);
      return result;
    } catch (error) {
      throw new Error('Error al actualizar productos');
    } finally {
      setLoading(false);
    }
  };

  const create = async (file: File) => {
    const format = file.name.split('.').pop();

    if (!AVAILABLE_PRODUCT_BATCH_LOAD_FORMATS.includes(format)) {
      throw new Error('Formato de archivo no soportado');
    }

    setLoading(true);
    try {
      const uploadResult = await uploadFileService(file, {
        filePrefix: 'im/batch-product/create',
        resourceType: 'XLSX',
        temp: true,
      });
      const [fileUploaded] = uploadResult;
      const result = await createProductsBatchService({
        fileId: fileUploaded.id,
      });

      setBatchTaskCreate(result);
      return result;
    } catch (error) {
      console.error(error);
      throw new Error('Error al crear productos');
    } finally {
      setLoading(false);
    }
  };

  return {
    update,
    create,
    loading,
  };
}

const batchCreateTaskSelector = (state: RootState) =>
  state.productBatchLoad.batchTaskCreate;

export function useProductBatchLoadCreateState(): {
  batchLoadTask?: ProductBatchLoadTask,
  loading: boolean,
} {
  const batchLoadTask = useSelector(batchCreateTaskSelector);
  const setBatchLoadTask = useActions(actions.setBatchTaskCreate);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    getProductsBatchCreateStatusService()
      .then(setBatchLoadTask)
      .catch(error => {
        console.error('Error getting batch create status', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [setBatchLoadTask]);

  useEffect(() => {
    if (!batchLoadTask || batchLoadTask?.state !== 'PENDING') return;

    let interval = setInterval(() => {
      getProductsBatchCreateStatusService()
        .then(setBatchLoadTask)
        .catch(error => {
          console.error('Error getting batch create status', error);
        })
        .finally(() => {
          setLoading(false);
        });
    }, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [batchLoadTask, setBatchLoadTask]);

  return {
    batchLoadTask,
    loading,
  };
}

const batchUpdateTaskSelector = (state: RootState) =>
  state.productBatchLoad.batchTaskUpdate;

export function useProductBatchLoadUpdateState(): {
  batchLoadTask?: ProductBatchLoadTask,
  loading: boolean,
} {
  const batchLoadTask = useSelector(batchUpdateTaskSelector);
  const setBatchLoadTask = useActions(actions.setBatchTaskUpdate);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    getProductsBatchEditStatusService()
      .then(setBatchLoadTask)
      .catch(error => {
        console.error('Error getting batch edit status', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [setBatchLoadTask]);

  useEffect(() => {
    if (!batchLoadTask || batchLoadTask?.state !== 'PENDING') return;

    let interval = setInterval(() => {
      getProductsBatchEditStatusService()
        .then(setBatchLoadTask)
        .catch(error => {
          console.error('Error getting batch edit status', error);
        })
        .finally(() => {
          setLoading(false);
        });
    }, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [batchLoadTask, setBatchLoadTask]);

  return {
    batchLoadTask,
    loading,
  };
}

export function useProductBatchLoadResults() {}
